import React, {useState, useEffect} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, Card, Grid2 as Grid, Link, Stack, Typography, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

import ReportAmendReasonBlock from '../components/ReportAmendReasonBlock';

//import schedules
import ReportAmendedReasonBlock from '../components/ReportAmendedReasonBlock';
import BankExpenditureItemizationRpeortContent from '../../../types/reports/bankExpenditureItemizationReportContent';
import BankExpenditureItemizationScheduleTable from '../components/BankExpenditureItemizationScheduleTable';
import BankExpenditureItemizationItem from '../../../types/reports/bankExpenditureItemizationItem';
import changeLogCategory from '../../../types/reports/changeLogCategory';
import ChangeLogScheduleTable from '../components/ChangeLogScheduleTable';

interface ReportProps {
   reportId: number;
   currentReport?: any
}

const BankExpenditureItemizationReport = (reportProps: ReportProps): JSX.Element => {

    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const [scheduleValue, setScheduleValue] = useState('summary');
    const fieldWidth = { xs: "50%", sm: "40%", md: "35%" };

    const [tableRows, setRows] = useState<BankExpenditureItemizationItem[]>();
    const [report, setReport] = useState<BankExpenditureItemizationRpeortContent>();
    const [diffs, setDiffs] = useState<changeLogCategory[]>();
    const [diffsCount, setDiffsCount] = useState<number>();

    useEffect(() => {
            if(reportProps.currentReport){
                let report = reportProps.currentReport;
                setScheduleValue('summary');
                setReport(report);
                setRows(report.items);
                setDiffs(report.diffs);
            }
            else{
                getFullReport();
            }
    }, [reportProps.reportId])

    const getFullReport = async () => {

        try{
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/report/${reportProps.reportId}`)
            .then(result => {
            if (!result.ok) {
                showBoundary(new Error('Failed to get bank expenditure itemization report content.'));
            }
            return result.json();
        })
        .then(result => {
            let report = result;
            setScheduleValue('summary');
            setReport(report);
            setRows(report.items);
            setDiffs(report.diffs);
            return;
        });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
    }

  const handleScheduleChange = (event: React.SyntheticEvent, newValue: string) => {
    setScheduleValue(newValue);
  };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={scheduleValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleScheduleChange} aria-label="expenditures schedules">
            <Tab className="report-tab-label" label="Summary" value="summary" />
            {
                (report?.items?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Items [${report?.items?.length}]`} value="items" />
            }
            {
                 report?.diffs &&
                 <Tab className="report-tab-label" label={`Amended Items`} value="changes" />
            }
          </TabList>
        </Box>
        <TabPanel value="summary">
            <Grid container spacing={2}>
                {/*Filer Info*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filer:
                                </Typography>
                                <Typography component={Link} underline='hover' target="_blank" color="primary" href={`/filers?q=${report?.cpfId}`} variant={'body1'} fontWeight={550}>
                                    {report?.filerFullName} ({report?.cpfId})
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filed On:
                                </Typography>
                                <Typography variant={'body1'}>
                                     {report?.dateFiled}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
                {/*Amendment Reason*/}
                {
                    report?.isAmendment && 
                    <ReportAmendReasonBlock reason={report?.amendmentReason} previousReportId={report?.previousReportId}></ReportAmendReasonBlock>
                }
                {/*Being Amended*/}
                {
                    report?.isAmended && 
                    <ReportAmendedReasonBlock nextReportId={report?.nextReportId}></ReportAmendedReasonBlock>
                }
                {/*Financial Summary*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Expenditure Date:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                    {report?.endDate}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Expenditure Vendor:
                                </Typography>
                                <Typography variant={'body1'} fontWeight={550} align='right'>
                                    {report?.vendorName}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} fontWeight={650} variant={'body1'}>
                                    Total Expenditure Amount:
                                </Typography>
                                <Typography variant={'body1'} fontWeight={550} align='right'>
                                    {report?.expenditureTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Candidates/Position:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                    <p dangerouslySetInnerHTML={{__html: `${report?.candidateSpendingBreakdown}`}} />
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </TabPanel>
        <TabPanel value="items">
            <BankExpenditureItemizationScheduleTable items={tableRows}></BankExpenditureItemizationScheduleTable>
            <Box padding={{ xs: 2, sm: 3 }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Total Expenditure:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.expenditureTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="changes">
            <ChangeLogScheduleTable changes={diffs}></ChangeLogScheduleTable>
        </TabPanel>
      </TabContext>
    </Box>
  );
}

export default BankExpenditureItemizationReport;